/**
 * V10 Phase C — `asOf` 전역 상태 훅.
 *
 * Timeline Playback slider / Edge Inspector datetime picker / URL (`?asOf=ISO`) 가 공유하는
 * 단일 소스. React Context + URL sync 로 구현 (외부 의존성 최소화).
 *
 * **핵심 규약**:
 * - **SPA 초기 렌더 URL race**: `isHydrated` 가 true 가 될 때까지 default now 로 fetch 하지 않음.
 *   URL 파라미터 파싱이 완료된 후에만 `asOf` 를 소비하도록 `enabled: isHydrated` 게이트를 걸 것.
 * - `setAsOf(undefined)` → "현재 시점" (URL 에서도 `asOf` 쿼리 삭제).
 * - `setAsOf("ISO")` → slider/picker 에서 선택한 시점. URL 에 `?asOf=ISO` 반영.
 */

import type { ReactNode } from "react"
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"

type AsOfState = {
	/** ISO 8601 datetime 또는 undefined (현재 시점). */
	readonly asOf: string | undefined
	/** URL 초기 파싱이 완료된 후 true. false 인 동안은 fetch 게이트를 걸어야 함. */
	readonly isHydrated: boolean
	setAsOf: (next: string | undefined) => void
}

const AsOfContext = createContext<AsOfState | undefined>(undefined)

/** 현재 URL 의 `?asOf=` 쿼리에서 ISO 문자열 추출. 유효하지 않으면 undefined. */
function parseAsOfFromUrl(): string | undefined {
	if (typeof window === "undefined") return undefined
	try {
		const url = new URL(window.location.href)
		const raw = url.searchParams.get("asOf")
		if (!raw) return undefined
		const ms = Date.parse(raw)
		if (!Number.isFinite(ms)) return undefined
		return raw
	} catch {
		return undefined
	}
}

/** URL 쿼리 `asOf` 를 갱신 — History replaceState 로 브라우저 뒤로가기 히스토리를 오염시키지 않음. */
function updateAsOfInUrl(next: string | undefined): void {
	if (typeof window === "undefined") return
	try {
		const url = new URL(window.location.href)
		if (next) {
			url.searchParams.set("asOf", next)
		} else {
			url.searchParams.delete("asOf")
		}
		window.history.replaceState(null, "", url.toString())
	} catch {
		// no-op (sandboxed iframe 등)
	}
}

export function AsOfProvider(props: { readonly children: ReactNode }): JSX.Element {
	const [asOf, setAsOfState] = useState<string | undefined>(undefined)
	const [isHydrated, setIsHydrated] = useState(false)
	const didHydrateRef = useRef(false)

	// 초기 URL 파싱 1회
	useEffect(() => {
		if (didHydrateRef.current) return
		didHydrateRef.current = true
		const parsed = parseAsOfFromUrl()
		setAsOfState(parsed)
		setIsHydrated(true)
	}, [])

	const setAsOf = useCallback((next: string | undefined) => {
		setAsOfState(next)
		updateAsOfInUrl(next)
	}, [])

	const value = useMemo<AsOfState>(() => ({ asOf, isHydrated, setAsOf }), [asOf, isHydrated, setAsOf])

	return <AsOfContext.Provider value={value}>{props.children}</AsOfContext.Provider>
}

export function useAsOf(): AsOfState {
	const ctx = useContext(AsOfContext)
	if (!ctx) {
		// Provider 바깥에서 호출된 경우 — 안전하게 "always current, hydrated" 기본값 반환.
		// 이 경로로는 GraphTab 외부에서 호출하지 말 것 (ServiceDetailPage 가 감싼다).
		return { asOf: undefined, isHydrated: true, setAsOf: () => undefined }
	}
	return ctx
}
